export interface PublicUploadInitResponse {
  sessionId: string;
  publicToken: string;
  chunkSize: number;
  totalChunks: number;
}

export interface PublicUploadCompleteResponse {
  jobId: string;
  publicToken: string;
}

interface RetryOptions {
  maxRetries?: number;
  delayMs?: number;
}

async function withRetry<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const maxRetries = options.maxRetries ?? 3;
  const delayMs = options.delayMs ?? 500;

  let lastError: unknown = null;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt === maxRetries) break;
      await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Upload failed');
}

async function sha256(blob: Blob): Promise<string> {
  const data = await blob.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', data);

  return Array.from(new Uint8Array(hash))
    .map((x) => x.toString(16).padStart(2, '0'))
    .join('');
}

export async function initPublicUpload(file: File): Promise<PublicUploadInitResponse> {
  const response = await fetch('/api/tools/free-audio-to-text/upload/init', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type || 'application/octet-stream',
    }),
  });

  // SAFETY: The surrounding typed contract establishes the asserted representation.
  const payload = (await response.json()) as {
    success: boolean;
    data?: PublicUploadInitResponse;
    error?: string;
  };

  if (!response.ok || !payload.success || !payload.data) {
    throw new Error(payload.error ?? 'Failed to start upload');
  }

  return payload.data;
}

export async function uploadPublicChunks(
  file: File,
  session: PublicUploadInitResponse,
  onProgress?: (percent: number) => void
): Promise<void> {
  const totalChunks = Math.ceil(file.size / session.chunkSize);
  let uploaded = 0;

  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
    const start = chunkIndex * session.chunkSize;
    const end = Math.min(start + session.chunkSize, file.size);
    const blob = file.slice(start, end);
    const chunkHash = await sha256(blob);

    await withRetry(async () => {
      const formData = new FormData();
      formData.set('sessionId', session.sessionId);
      formData.set('publicToken', session.publicToken);
      formData.set('chunkIndex', String(chunkIndex));
      formData.set('chunkHash', chunkHash);
      formData.set('chunk', blob);

      const response = await fetch('/api/tools/free-audio-to-text/upload/chunk', {
        method: 'POST',
        body: formData,
      });

      // SAFETY: The surrounding typed contract establishes the asserted representation.
      const payload = (await response.json()) as { success: boolean; error?: string };

      if (!response.ok || !payload.success) {
        throw new Error(payload.error ?? 'Failed to upload chunk');
      }
    });

    uploaded += 1;
    onProgress?.(Math.round((uploaded / totalChunks) * 100));
  }
}

export async function completePublicUpload(session: PublicUploadInitResponse): Promise<PublicUploadCompleteResponse> {
  const response = await fetch('/api/tools/free-audio-to-text/upload/complete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId: session.sessionId,
      publicToken: session.publicToken,
    }),
  });

  // SAFETY: The surrounding typed contract establishes the asserted representation.
  const payload = (await response.json()) as {
    success: boolean;
    data?: PublicUploadCompleteResponse;
    error?: string;
  };

  if (!response.ok || !payload.success || !payload.data) {
    throw new Error(payload.error ?? 'Failed to finalize upload');
  }

  return payload.data;
}
